import { Form, Input, Radio } from 'antd';

import { FormInstance, useForm } from 'antd/lib/form/Form';
import { createUpdateInvoiceHeaderList } from '../../../../../api/billing-center/invoice';
import {
  messageError,
  messageSuccess,
} from '../../../../../components/message';
import styles from './index.module.scss';

import getFormatMessage from '../../../../../locales/get-format-message';
import { IAPICompanyFullInfo } from '../../../../../model/api-modules';

import {
  IAPICreateUpdateInvoiceInfo,
  IAPIInvoiceHeader,
  InvoiceTarget,
} from '../../../../../model/api-modules/bill-center';
import { useEffect, useState } from 'react';
import { ConsoleDrawer } from '../../../../../components/drawer';
import { ConsoleForm } from '../../../../../components/form';
import { SpriteSvg, SpriteSvgType } from '../../../../../components/sprite-svg';
import { getInvoiceTypeText, INVOICE_TYPE_OPTIONS } from '../lib';
import { CommonPrompt } from '../../../../../components/prompt';
import { CommonButton } from '../../../../../components/button/main';
import { PermissionCheck } from '../../../permission-check';
import { RbacRoleObjectKey } from '../../../../../model';
interface IProps {
  readonly defaultEdit?: boolean;
  readonly companyInfo: IAPICompanyFullInfo;
  readonly invoiceInfo?: IAPIInvoiceHeader;
  readonly onChange: (info: IAPIInvoiceHeader) => void;
  readonly onClose: () => void;
}

export const AddModifyInvoiceDrawer = (props: IProps) => {
  const {
    defaultEdit = false,
    companyInfo,
    invoiceInfo,
    onChange,
    onClose,
  } = props;

  const [editMode, setEditMode] = useState<boolean>(defaultEdit);
  const [form] = useForm<IAPICreateUpdateInvoiceInfo>();

  useEffect(() => {
    init();
  }, [editMode, invoiceInfo]);

  const init = () => {
    if (invoiceInfo && editMode) {
      form.setFieldsValue(invoiceInfo);
    }
  };
  const submit = async () => {
    const data = await form.validateFields();
    data.invoice_id = invoiceInfo?.invoice_id;
    data.studio_id = companyInfo.StudioID;

    const apiRes = await createUpdateInvoiceHeaderList(data);
    if (!apiRes) {
      messageError(getFormatMessage('save_failed'));
      return false;
    }
    messageSuccess(getFormatMessage('save_success'));
    onChange({
      ...data,
      invoice_id: invoiceInfo?.invoice_id || '',
      create_time: invoiceInfo?.create_time || '',
      update_time: new Date().toISOString(),
    });
    return true;
  };

  return (
    <ConsoleDrawer
      className={styles.drawer}
      onClose={onClose}
      title={getFormatMessage('invoice_header_title')}
      onOk={submit}
      okButton={
        editMode ? undefined : (
          <PermissionCheck permissionKey={RbacRoleObjectKey.FeeCenterEdit}>
            <CommonButton
              style={{ minWidth: 88 }}
              onClick={() => setEditMode(true)}
            >
              <SpriteSvg
                type={SpriteSvgType.Edit}
                className={styles.editIcon}
              />
              {getFormatMessage('edit')}
            </CommonButton>
          </PermissionCheck>
        )
      }
      cancelText={getFormatMessage('close')}
    >
      <>
        {editMode && (
          <CommonPrompt className={styles.prompt}>
            <span>{getFormatMessage('invoice_header_warn')}</span>
          </CommonPrompt>
        )}
        <HeaderForm
          companyInfo={companyInfo}
          form={form}
          invoiceInfo={invoiceInfo}
          editMode={editMode}
        />
      </>
    </ConsoleDrawer>
  );
};

export const HeaderForm = (props: {
  readonly className?: string;
  readonly companyInfo?: IAPICompanyFullInfo;
  readonly form?: FormInstance<IAPICreateUpdateInvoiceInfo>;
  readonly invoiceInfo?: IAPIInvoiceHeader;
  readonly editMode?: boolean;
}) => {
  const { className, invoiceInfo, editMode, form, companyInfo } = props;
  return (
    <ConsoleForm form={form} className={className}>
      <Form.Item
        label={getFormatMessage('invoice_header_type')}
        name="invoice_target"
        initialValue={InvoiceTarget.Enterprise}
        rules={[
          {
            required: editMode,
          },
        ]}
      >
        {editMode ? (
          <Radio.Group
            options={[
              {
                label: getFormatMessage('enterprise'),
                value: InvoiceTarget.Enterprise,
              },
            ]}
          />
        ) : (
          getFormatMessage('enterprise')
        )}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('invoice_type')}
        name="invoice_type"
        rules={[
          {
            required: editMode,
          },
        ]}
      >
        {!editMode ? (
          getInvoiceTypeText(invoiceInfo?.invoice_type)
        ) : (
          <Radio.Group
            className={styles.invoiceTypeSelect}
            options={INVOICE_TYPE_OPTIONS}
          />
        )}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('invoice_header_name')}
        name="invoice_name"
        initialValue={companyInfo?.Name}
        rules={[
          {
            required: editMode,
          },
        ]}
      >
        {!editMode ? invoiceInfo?.invoice_name : <Input disabled={true} />}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('invoice_header_code')}
        name="identification_code"
        rules={[
          {
            required: editMode,
            validator: (rule, value, callback) => {
              if (!/^[0-9A-Z]{15,20}$/.test(value)) {
                callback(getFormatMessage('company_input_wrong'));
              }
              callback();
            },
          },
        ]}
      >
        {editMode ? (
          <Input placeholder={getFormatMessage('invoice_id_code_info')} />
        ) : (
          invoiceInfo?.identification_code
        )}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('invoice_bank')}
        name="open_bank"
        rules={[
          {
            required: editMode,
            validator: (rule, value, callback) => {
              if (!/^[\u4e00-\u9fa5A-Za-z\s\d]+$/.test(value)) {
                callback(getFormatMessage('company_input_wrong'));
              }

              callback();
            },
          },
        ]}
      >
        {editMode ? (
          <Input
            placeholder={getFormatMessage('invoice_bank_info')}
            maxLength={50}
          />
        ) : (
          invoiceInfo?.open_bank
        )}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('invoice_bank_account')}
        name="open_account"
        rules={[
          {
            required: editMode,
            validator: (rule, value, callback) => {
              if (!/^[\u4e00-\u9fa5A-Za-z\s\d]+$/.test(value)) {
                callback(getFormatMessage('company_input_wrong'));
              }

              callback();
            },
          },
        ]}
      >
        {editMode ? (
          <Input
            placeholder={getFormatMessage('invoice_bank_account_info')}
            maxLength={50}
          />
        ) : (
          invoiceInfo?.open_account
        )}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('invoice_address')}
        name="enterprise_address"
        rules={[
          {
            required: editMode,
            validator: (rule, value, callback) => {
              if (!/^[\u4e00-\u9fa5A-Za-z\s\d]+$/.test(value)) {
                callback(getFormatMessage('company_input_wrong'));
              }

              callback();
            },
          },
        ]}
      >
        {editMode ? (
          <Input
            placeholder={getFormatMessage('invoice_cert_info')}
            maxLength={100}
          />
        ) : (
          invoiceInfo?.enterprise_address
        )}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('invoice_phone')}
        name="enterprise_phone"
        rules={[
          {
            required: editMode,
            validator: (rule, value, callback) => {
              if (!/^\+?[\d]+$/.test(value)) {
                callback(getFormatMessage('company_input_wrong'));
              }

              callback();
            },
          },
        ]}
      >
        {editMode ? (
          <Input
            placeholder={getFormatMessage('invoice_phone_info')}
            maxLength={20}
          />
        ) : (
          invoiceInfo?.enterprise_phone
        )}
      </Form.Item>
    </ConsoleForm>
  );
};
